import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  BrowserRouter,
  Routes,
  Route,
  Outlet,
  useParams,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LOCALE_PREFIXES } from "@/i18n";
import { AuthProvider } from "@/hooks/useAuth";
import { useHtmlLang } from "@/hooks/useHtmlLang";
import MobileCTA from "@/components/layout/MobileCTA";
import CookieBanner from "@/components/layout/CookieBanner";
import SkipLink from "@/components/layout/SkipLink";
import FloatingPhone from "@/components/layout/FloatingPhone";
import ScrollToTop from "@/components/layout/ScrollToTop";
import LanguageSelector from "./pages/LanguageSelector";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Onboarding from "./pages/Onboarding";
import Inquiry from "./pages/Inquiry";
import InquiryDanke from "./pages/InquiryDanke";
import Dashboard from "./pages/Dashboard";
import NotFound from "./pages/NotFound";
import OperatorDashboard from "./pages/operator/OperatorDashboard";
import OperatorRequests from "./pages/operator/OperatorRequests";
import OperatorProviders from "./pages/operator/OperatorProviders";
import OperatorProviderCreate from "./pages/operator/OperatorProviderCreate";
import OperatorProviderDetail from "./pages/operator/OperatorProviderDetail";
import OperatorOpportunities from "./pages/operator/OperatorOpportunities";
import OperatorClaims from "./pages/operator/OperatorClaims";
import OperatorBilling from "./pages/operator/OperatorBilling";
import OperatorCostOverview from "./pages/operator/OperatorCostOverview";
import OperatorPricing from "./pages/operator/OperatorPricing";
import OperatorSettings from "./pages/operator/OperatorSettings";
import OperatorProfile from "./pages/operator/OperatorProfile";
import OperatorMatchingSettings from "./pages/operator/OperatorMatchingSettings";
import OperatorSalesDashboard from "./pages/operator/OperatorSalesDashboard";
import OperatorROICalculator from "./pages/operator/OperatorROICalculator";
import OperatorFeedback from "./pages/operator/OperatorFeedback";
import ProviderProfileEdit from "./pages/ProviderProfileEdit";
import Feedback from "./pages/Feedback";
import AnfrageZurueckziehen from "./pages/AnfrageZurueckziehen";
import Impressum from "./pages/Impressum";
import Datenschutz from "./pages/Datenschutz";
import Kontakt from "./pages/Kontakt";
import KontaktDanke from "./pages/KontaktDanke";
import FuerHandwerker from "./pages/FuerHandwerker";
import WarumCraftfindus from "./pages/WarumCraftfindus";
import Qualitaetsversprechen from "./pages/Qualitaetsversprechen";
import Registrieren from "./pages/Registrieren";
import RegistrierenDanke from "./pages/RegistrierenDanke";
import OperatorRegistrations from "./pages/operator/OperatorRegistrations";
import Leads from "./pages/Leads";
import Suche from "./pages/Suche";
import UeberUns from "./pages/UeberUns";
import Barrierefreiheit from "./pages/Barrierefreiheit";
import CraftPricing from "./pages/CraftPricing";
import ProviderBilling from "./pages/ProviderBilling";
import ProviderInvoices from "./pages/ProviderInvoices";
import SEOLandingPage from "./pages/SEOLandingPage";
import { BlogList, BlogPost } from "./pages/Blog";
import OperatorSEO from "./pages/operator/OperatorSEO";
import ProviderAnalytics from "./pages/ProviderAnalytics";

const queryClient = new QueryClient();

/** Syncs i18n language with the URL locale prefix and renders child routes */
function LocaleLayout() {
  const { locale } = useParams<{ locale: string }>();
  const { i18n } = useTranslation();

  const config = LOCALE_PREFIXES.find((p) => p.prefix === locale);

  useEffect(() => {
    if (config && i18n.language !== config.i18nCode) {
      i18n.changeLanguage(config.i18nCode);
      localStorage.setItem("craftfindus_lang", config.i18nCode);
      localStorage.setItem("craftfindus_locale_prefix", config.prefix);
    }
  }, [config, i18n, locale]);

  if (!config) return <Navigate to="/de/" replace />;
  return <Outlet />;
}

/** Redirects old un-prefixed URLs → /de/… (client-side 301-equivalent) */
function LegacyRedirect() {
  const location = useLocation();
  return (
    <Navigate
      to={`/de${location.pathname}${location.search}${location.hash}`}
      replace
    />
  );
}

const App = () => {
  useHtmlLang();
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <SkipLink />
            <Routes>
              {/* Root: language / country selector (noindex, follow) */}
              <Route path="/" element={<LanguageSelector />} />

              {/* ── Locale-prefixed routes ── */}
              <Route path="/:locale" element={<LocaleLayout />}>
                <Route index element={<Index />} />
                <Route path="auth" element={<Auth />} />
                <Route path="onboarding" element={<Onboarding />} />
                <Route path="inquiry" element={<Inquiry />} />
                <Route path="inquiry/danke" element={<InquiryDanke />} />
                <Route path="dashboard" element={<Dashboard />} />
                {/* Operator Portal */}
                <Route path="operator" element={<OperatorDashboard />} />
                <Route path="operator/registrations" element={<OperatorRegistrations />} />
                <Route path="operator/requests" element={<OperatorRequests />} />
                <Route path="operator/providers" element={<OperatorProviders />} />
                <Route path="operator/providers/new" element={<OperatorProviderCreate />} />
                <Route path="operator/providers/:providerId" element={<OperatorProviderDetail />} />
                <Route path="operator/opportunities" element={<OperatorOpportunities />} />
                <Route path="operator/claims" element={<OperatorClaims />} />
                <Route path="operator/billing" element={<OperatorBilling />} />
                <Route path="operator/costs" element={<OperatorCostOverview />} />
                <Route path="operator/pricing" element={<OperatorPricing />} />
                <Route path="operator/settings" element={<OperatorSettings />} />
                <Route path="operator/matching" element={<OperatorMatchingSettings />} />
                <Route path="operator/profile" element={<OperatorProfile />} />
                <Route path="operator/sales" element={<OperatorSalesDashboard />} />
                <Route path="operator/roi" element={<OperatorROICalculator />} />
                <Route path="operator/feedback" element={<OperatorFeedback />} />
                <Route path="operator/seo" element={<OperatorSEO />} />
                <Route path="feedback/:token" element={<Feedback />} />
                <Route path="anfrage-zurueckziehen/:token" element={<AnfrageZurueckziehen />} />
                <Route path="profile/edit" element={<ProviderProfileEdit />} />
                <Route path="impressum" element={<Impressum />} />
                <Route path="datenschutz" element={<Datenschutz />} />
                <Route path="barrierefreiheit" element={<Barrierefreiheit />} />
                <Route path="kontakt" element={<Kontakt />} />
                <Route path="kontakt/danke" element={<KontaktDanke />} />
                <Route path="fuer-handwerker" element={<FuerHandwerker />} />
                <Route path="fuer-it-unternehmer" element={<FuerHandwerker />} />
                <Route path="warum-craftfindus" element={<WarumCraftfindus />} />
                <Route path="qualitaetsversprechen" element={<Qualitaetsversprechen />} />
                <Route path="registrieren" element={<Registrieren />} />
                <Route path="registrieren/danke" element={<RegistrierenDanke />} />
                <Route path="leads" element={<Leads />} />
                <Route path="suche" element={<Suche />} />
                <Route path="ueber-uns" element={<UeberUns />} />
                <Route path="craft/pricing" element={<CraftPricing />} />
                <Route path="provider/billing" element={<ProviderBilling />} />
                <Route path="provider/invoices" element={<ProviderInvoices />} />
                <Route path="provider/analytics" element={<ProviderAnalytics />} />
                <Route path="blog" element={<BlogList />} />
                <Route path="blog/:slug" element={<BlogPost />} />
                <Route path=":slug" element={<SEOLandingPage />} />
                <Route path="*" element={<NotFound />} />
              </Route>

              {/* Legacy redirect: old un-prefixed URLs → /de/… */}
              <Route path="*" element={<LegacyRedirect />} />
            </Routes>
            <MobileCTA />
            <FloatingPhone />
            <CookieBanner />
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
